/** Normalize route config to [{ branch, target_node_id }] */

export function routesConfigToList(routes) {
  if (Array.isArray(routes)) {
    return routes
      .map((r) => ({
        branch: String(r?.branch ?? r?.match ?? r?.value ?? '').trim(),
        target_node_id: r?.target_node_id || r?.to || '',
      }))
      .filter((r) => r.branch || r.target_node_id);
  }
  if (routes && typeof routes === 'object') {
    return Object.entries(routes).map(([branch, target]) => ({
      branch: String(branch).trim(),
      target_node_id: typeof target === 'string' ? target : target?.target_node_id || '',
    }));
  }
  return [];
}

export function routesListToConfig(list) {
  return (list || []).map((r) => ({
    branch: String(r?.branch ?? '').trim(),
    target_node_id: r?.target_node_id || '',
  }));
}

/** Read existing route edges from the graph */
export function routesFromEdges(definition, nodeId) {
  return (definition?.edges || [])
    .filter((e) => e.from === nodeId && e.event === 'route')
    .map((e) => ({
      branch: String(e.match?.branch ?? e.match?.payload ?? '').trim(),
      target_node_id: e.to || '',
    }))
    .filter((r) => r.branch || r.target_node_id);
}

/** Suggest branch match values from template buttons that capture the same var */
export function suggestBranchValues(definition, varName) {
  if (!varName) return [];
  const seen = new Set();
  const add = (v) => {
    const s = String(v ?? '').trim();
    if (s && !seen.has(s.toLowerCase())) {
      seen.add(s.toLowerCase());
      return s;
    }
    return null;
  };
  const out = [];

  (definition?.nodes || []).forEach((node) => {
    if (
      node.type !== 'message.template' &&
      node.type !== 'message.interactive_buttons'
    ) {
      return;
    }
    const nodeVar = (node.config?.capture_var || '').trim();
    (node.config?.buttons || []).forEach((btn) => {
      const edgeVar = (btn.capture_var || nodeVar || '').trim();
      if (edgeVar === varName) {
        const val = add(btn.id || btn.label);
        if (val) out.push(val);
      }
    });
  });

  (definition?.edges || []).forEach((e) => {
    if (e.event === 'button_reply' && e.capture?.var === varName && e.match?.payload) {
      const val = add(e.match.payload);
      if (val) out.push(val);
    }
  });

  return out;
}

/** ai.classify intents ↔ route edges (branch = intent label) */

export function intentsConfigToList(intents) {
  if (!Array.isArray(intents)) return [];
  return intents
    .map((intent) => ({
      label: String(intent?.label ?? intent?.branch ?? '').trim(),
      description: String(intent?.description ?? '').trim(),
      target_node_id: intent?.target_node_id || intent?.to || '',
    }))
    .filter((intent) => intent.label || intent.target_node_id);
}

export function intentsListToConfig(list) {
  return (list || []).map((intent) => ({
    label: String(intent?.label ?? '').trim(),
    description: String(intent?.description ?? '').trim(),
    target_node_id: intent?.target_node_id || '',
  }));
}

export function intentsToRoutes(intents) {
  return intentsConfigToList(intents).map((intent) => ({
    branch: intent.label,
    target_node_id: intent.target_node_id,
  }));
}

export function intentsFromEdges(definition, nodeId) {
  return routesFromEdges(definition, nodeId).map((route) => ({
    label: route.branch,
    description: '',
    target_node_id: route.target_node_id,
  }));
}

/** Shared route-edge sync for logic.route_by_var and ai.classify */
export function applyRouteEdges(definition, nodeId, routes) {
  const routeTargets = new Set(routes.map((r) => r.target_node_id).filter(Boolean));

  let edges = (definition?.edges || []).filter((e) => {
    if (e.from === nodeId && e.event === 'route') return false;
    if (e.from === nodeId && e.event === 'completed' && routeTargets.has(e.to)) {
      return false;
    }
    if (e.event === 'completed' && routeTargets.has(e.from) && routeTargets.has(e.to)) {
      return false;
    }
    return true;
  });

  routes.forEach((r) => {
    const branch = (r.branch || '').trim();
    if (branch && r.target_node_id) {
      edges.push({
        from: nodeId,
        to: r.target_node_id,
        event: 'route',
        match: { branch },
      });
    }
  });

  return { ...definition, edges };
}
